(() => {
  'use strict';
  const initialize = (root) => {
    if (root.dataset.mwReady) return;
    root.dataset.mwReady = 'true';
    const storageKey = `modello-withdrawal:${root.id}:${window.location.pathname}`;
    const apiStorageKey = `modello-withdrawal-api:${root.id}:${window.location.pathname}`;
    const apiMetadataTTL = 24 * 60 * 60 * 1000;
    const freshMetadata = (metadata) => metadata && Number.isFinite(metadata.createdAt)
      && Date.now() >= metadata.createdAt && Date.now() - metadata.createdAt < apiMetadataTTL;
    const accepted = root.querySelector('[data-mw-accepted]');
    if (accepted) {
      // Only Shopify's server-rendered posted_successfully? branch reaches here.
      try {
        const metadata = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        sessionStorage.removeItem(storageKey);
        if (metadata && typeof metadata.reference === 'string' &&
            /^MW-[A-Z0-9-]{8,80}$/.test(metadata.reference) &&
            Date.now() - metadata.createdAt >= 0 && Date.now() - metadata.createdAt < 3600000) {
          root.querySelector('[data-mw-accepted-reference]').textContent = metadata.reference;
          root.querySelector('[data-mw-reference-output]').hidden = false;
        }
      } catch { /* Storage is optional; the native form remains usable. */ }
      accepted.focus();
      return;
    }
    const form = root.querySelector('form');
    if (!form) return;
    const fields = Object.fromEntries([...root.querySelectorAll('[data-mw-field]')]
      .map((field) => [field.dataset.mwField, field]));
    const firstStep = root.querySelector('[data-mw-step="1"]');
    const reviewStep = root.querySelector('[data-mw-step="2"]');
    const body = root.querySelector('[data-mw-body]');
    const reference = root.querySelector('[data-mw-reference]');
    const timestamp = root.querySelector('[data-mw-timestamp]');
    const preview = root.querySelector('[data-mw-preview]');
    const endpoint = (root.dataset.mwEndpoint || '').trim();
    const siteKey = (root.dataset.mwSiteKey || '').trim();
    const apiStatus = root.querySelector('[data-mw-api-status]');
    const apiError = root.querySelector('[data-mw-api-error]');
    const content = root.querySelector('[data-mw-content]');
    if (!firstStep || !reviewStep || !body || !reference || !timestamp || !preview) return;
    let step = 1;
    let inFlight = false;
    let completed = false;
    let submission = null;
    let retrySubmission = null;
    let apiRecord = null;
    let securityToken = '';
    let widgetId;
    let renderingWidget = false;
    const securityError = root.querySelector('[data-mw-security-error]');
    const loadSecurity = () => {
      if (window.turnstile) return Promise.resolve();
      if (window.__modelloTurnstileReady) return window.__modelloTurnstileReady;
      window.__modelloTurnstileReady = new Promise((resolve, reject) => {
        let script = document.getElementById('modello-turnstile-api');
        if (!script) {
          script = document.createElement('script');
          script.id = 'modello-turnstile-api';
          script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
          script.defer = true;
          document.head.appendChild(script);
        }
        script.addEventListener('load', resolve, { once: true });
        script.addEventListener('error', reject, { once: true });
      }).catch((error) => {
        window.__modelloTurnstileReady = null;
        document.getElementById('modello-turnstile-api')?.remove();
        throw error;
      });
      return window.__modelloTurnstileReady;
    };
    const resetSecurity = () => {
      securityToken = '';
      if (widgetId !== undefined && window.turnstile) window.turnstile.reset(widgetId);
    };
    const renderSecurity = async () => {
      if (!endpoint || !siteKey || renderingWidget || widgetId !== undefined) return;
      renderingWidget = true;
      try {
        await loadSecurity();
        widgetId = window.turnstile.render(root.querySelector('[data-mw-turnstile]'), {
          sitekey: siteKey, action: 'withdrawal', theme: 'light',
          size: window.innerWidth < 375 ? 'compact' : 'flexible',
          'response-field': false,
          callback: (token) => { securityToken = token; securityError.hidden = true; },
          'error-callback': () => { securityToken = ''; securityError.hidden = false; },
          'expired-callback': resetSecurity,
          'timeout-callback': resetSecurity
        });
      } catch { securityError.hidden = false; }
      finally { renderingWidget = false; }
    };
    const securityReady = () => {
      if (siteKey && securityToken) return true;
      if (securityError) { securityError.hidden = false; securityError.focus(); }
      void renderSecurity();
      return false;
    };
    const now = () => new Date().toISOString();
    const uuid = () => {
      if (window.crypto.randomUUID) return window.crypto.randomUUID();
      const bytes = window.crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 15) | 64;
      bytes[8] = (bytes[8] & 63) | 128;
      const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    };
    if (window.crypto && window.crypto.getRandomValues) {
      const random = window.crypto.getRandomValues(new Uint32Array(2));
      reference.value = `MW-${now().slice(0, 10).replace(/-/g, '')}-${[...random]
        .map((number) => number.toString(36).toUpperCase()).join('-')}`;
    }
    timestamp.value = now();
    const values = () => ({
      name: fields.name.value.trim(),
      order: fields.order.value.trim(),
      email: fields.email.value.trim(),
      scope: fields.scope.selectedOptions[0].textContent.trim(),
      products: fields.scope.value === 'partial'
        ? fields.products.value.trim() : (fields.products.value.trim() || root.dataset.mwAllProducts),
      reason: fields.reason.value.trim() || root.dataset.mwNotSpecified
    });
    const refresh = () => {
      const partial = fields.scope.value === 'partial';
      fields.products.required = partial;
      fields.products.setCustomValidity(partial && !fields.products.value.trim()
        ? fields.products.dataset.mwPartialError : '');
      root.querySelector('[data-mw-products-required]').hidden = !partial;
      const current = values();
      const lines = [root.dataset.mwDeclaration, ''];
      Object.entries(current).forEach(([key, value]) => {
        root.querySelector(`[data-mw-summary-value="${key}"]`).textContent = value || root.dataset.mwEmpty;
        const label = root.querySelector(`[data-mw-label="${key}"]`).textContent.trim();
        lines.push(`${label}: ${value}`);
      });
      lines.push('', `${reference.dataset.mwMetaLabel} ${reference.value}`,
        `${timestamp.dataset.mwMetaLabel} ${timestamp.value}`);
      body.value = lines.join('\n');
      preview.textContent = body.value;
    };
    const showStep = (next, focus = true) => {
      step = next;
      firstStep.hidden = next !== 1;
      reviewStep.hidden = next !== 2;
      root.querySelectorAll('[data-mw-progress]').forEach((item) => {
        if (Number(item.dataset.mwProgress) === next) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      if (focus) root.querySelector(next === 1 ? '[data-mw-step-heading]' : '[data-mw-review-heading]').focus();
      if (next === 2) void renderSecurity();
    };
    const valid = () => {
      ['name', 'order', 'email'].forEach((key) => { fields[key].value = fields[key].value.trim(); });
      refresh();
      if (form.checkValidity()) return true;
      showStep(1, false);
      form.reportValidity();
      return false;
    };
    const showError = () => {
      apiError.hidden = false;
      apiError.focus();
    };
    const prepareSubmission = async () => {
      const current = values();
      const website = root.querySelector('[data-mw-website]').value;
      const canonical = JSON.stringify({ name: current.name, email: current.email,
        order: current.order, products: current.products, reason: current.reason,
        declaration: root.dataset.mwDeclaration });
      const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical));
      const fingerprint = [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
      if (submission && submission.fingerprint === fingerprint && freshMetadata(submission)) return submission;
      let metadata;
      try {
        metadata = JSON.parse(sessionStorage.getItem(apiStorageKey) || 'null');
        if (!freshMetadata(metadata) || !/^[a-f0-9]{64}$/.test(metadata.fingerprint) ||
            !/^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(metadata.requestId)) {
          metadata = null;
          sessionStorage.removeItem(apiStorageKey);
        }
      } catch { metadata = null; }
      const reuse = metadata && metadata.fingerprint === fingerprint;
      const requestId = reuse ? metadata.requestId : uuid();
      const createdAt = reuse ? metadata.createdAt : Date.now();
      timestamp.value = now();
      refresh();
      // Persist only UUID, SHA-256 and age. No raw data, message, token or reference.
      submission = {
        fingerprint, createdAt,
        payload: {
          requestId, name: current.name, email: current.email,
          order: current.order, products: current.products, message: `${root.dataset.mwDeclaration}\n${root.querySelector('[data-mw-label="reason"]').textContent}: ${current.reason}`, website
        }
      };
      try {
        sessionStorage.setItem(apiStorageKey, JSON.stringify({ requestId, fingerprint, createdAt }));
      } catch { /* In-memory submission still deduplicates this page's retries. */ }
      return submission;
    };
    const sendToEndpoint = async (frozenRequest = null) => {
      if (inFlight || completed || !securityReady()) return;
      let url;
      try {
        url = new URL(endpoint);
        if (url.protocol !== 'https:' || url.username || url.password) throw new Error('Invalid endpoint');
      } catch { showError(); return; }
      const token = securityToken;
      securityToken = '';
      inFlight = true;
      apiError.hidden = true;
      const controls = [...form.querySelectorAll('button, input, textarea, select')];
      const disabledBefore = controls.map((control) => control.disabled);
      controls.forEach((control) => { control.disabled = true; });
      form.setAttribute('aria-busy', 'true');
      root.querySelector('[data-mw-sending]').hidden = false;
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 30000);
      try {
        // Lock/disable before the async digest so double clicks cannot race it.
        const request = frozenRequest || await prepareSubmission();
        const response = await fetch(url.href, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'omit', body: JSON.stringify({ ...request.payload, turnstileToken: token }), signal: controller.signal
        });
        const result = await response.json();
        const hasReference = typeof result.reference === 'string' && result.reference.length > 0 && result.reference.length < 160;
        if (!hasReference) { showError(); return; }
        apiRecord = Object.freeze({
          payload: Object.freeze({ ...request.payload }), reference: result.reference,
          submittedAt: typeof result.submittedAt === 'string' ? result.submittedAt : '',
          confirmationSent: result.confirmationSent === true, merchantCopySent: result.merchantCopySent === true
        });
        request.reference = result.reference;
        reference.value = result.reference;
        if (typeof result.submittedAt === 'string') timestamp.value = result.submittedAt;
        refresh();
        const success = response.ok && result.ok === true;
        const manualReview = !success && result.error === 'EMAIL_REVIEW_REQUIRED';
        completed = success || manualReview;
        retrySubmission = completed ? null : request;
        if (success) {
          try {
            const metadata = JSON.parse(sessionStorage.getItem(apiStorageKey) || 'null');
            if (metadata && metadata.requestId === request.payload.requestId) sessionStorage.removeItem(apiStorageKey);
          } catch { /* Optional storage. */ }
        }
        root.querySelector('[data-mw-api-title]').textContent = success ? root.dataset.mwSuccessTitle : root.dataset.mwPendingTitle;
        root.querySelector('[data-mw-api-message]').textContent = success ? root.dataset.mwSuccessText
          : manualReview ? root.dataset.mwManualReviewText
            : (result.confirmationSent === true ? root.dataset.mwMerchantPendingText : root.dataset.mwApiPendingText);
        root.querySelector('[data-mw-api-reference]').textContent = result.reference;
        root.querySelector('[data-mw-api-time]').textContent = typeof result.submittedAt === 'string'
          ? `${timestamp.dataset.mwMetaLabel} ${result.submittedAt}` : '';
        root.querySelector('[data-mw-api-email]').hidden = result.confirmationSent !== true;
        root.querySelector('[data-mw-api-retry]').hidden = completed;
        content.hidden = completed;
        apiStatus.hidden = false;
        apiStatus.focus();
      } catch { showError(); }
      finally {
        window.clearTimeout(timeout);
        controls.forEach((control, index) => { control.disabled = disabledBefore[index]; });
        form.removeAttribute('aria-busy');
        root.querySelector('[data-mw-sending]').hidden = true;
        inFlight = false;
        if (!completed) resetSecurity();
      }
    };
    root.querySelector('[data-mw-next]').addEventListener('click', () => {
      if (valid()) showStep(2);
    });
    root.querySelector('[data-mw-back]').addEventListener('click', () => showStep(1));
    form.addEventListener('input', (event) => {
      if (event.target.matches('[data-mw-field]')) {
        event.target.removeAttribute('aria-invalid');
        refresh();
      }
    });
    form.addEventListener('change', refresh);
    form.addEventListener('invalid', (event) => event.target.setAttribute('aria-invalid', 'true'), true);
    form.addEventListener('submit', (event) => {
      // Capture endpoint submissions before Shopify's contact/captcha bubble
      // handlers, so the enhanced mode cannot also submit to /contact.
      if (endpoint) { event.preventDefault(); event.stopImmediatePropagation(); }
      if (inFlight || completed) { event.preventDefault(); return; }
      if (!valid()) { event.preventDefault(); return; }
      if (step !== 2) { event.preventDefault(); showStep(2); return; }
      if (endpoint) {
        event.preventDefault();
        void sendToEndpoint();
        return;
      }
      timestamp.value = now();
      refresh();
      // No personal data is persisted. Keep only this tab's reference, removed
      // immediately after Shopify renders posted_successfully?. No double POST.
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ reference: reference.value, createdAt: Date.now() }));
      } catch { /* Native POST does not depend on storage. */ }
    }, Boolean(endpoint));
    root.querySelector('[data-mw-api-retry]').addEventListener('click', () => {
      if (retrySubmission) void sendToEndpoint(retrySubmission);
    });
    root.querySelectorAll('[data-mw-download]').forEach((button) => button.addEventListener('click', () => {
      const linesFor = (details) => Object.entries(details).map(([key, value]) =>
        `${root.querySelector(`[data-mw-label="${key}"]`).textContent.trim()}: ${value}`);
      let text;
      let fileReference;
      if (button.closest('[data-mw-api-status]')) {
        if (!apiRecord) return;
        const { payload, reference: recordedReference, submittedAt } = apiRecord;
        text = [payload.message, '', ...linesFor({ name: payload.name, order: payload.order,
          email: payload.email, products: payload.products }), '',
          `${reference.dataset.mwMetaLabel} ${recordedReference}`,
          submittedAt ? `${timestamp.dataset.mwMetaLabel} ${submittedAt}` : ''].join('\n');
        fileReference = recordedReference;
      } else {
        refresh();
        // A draft of edited fields must not borrow an earlier server reference.
        text = [root.dataset.mwDeclaration, '', ...linesFor(values()), '',
          `${timestamp.dataset.mwMetaLabel} ${now()}`].join('\n');
        fileReference = 'date-completate';
      }
      const note = root.querySelector('.mw__review .mw__small').textContent.trim();
      const file = new Blob([`${note}\n\n${text}\n`], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `modello-retragere-${fileReference.replace(/[^A-Z0-9-]/gi, '')}.txt`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }));
    refresh();
    // With JS, manual native constraint validation makes Enter open review first.
    // Without JS, the original contact form and HTML validation remain available.
    form.noValidate = true;
    root.querySelector('[data-mw-direct]').hidden = true;
    root.querySelector('[data-mw-next]').hidden = false;
    root.dataset.mwEnhanced = 'true';
    if (root.querySelector('[data-mw-server-error]')) root.querySelector('[data-mw-server-error]').focus();
  };
  const initializeWithin = (scope) => {
    if (scope.matches && scope.matches('[data-mw]')) initialize(scope);
    scope.querySelectorAll('[data-mw]').forEach(initialize);
  };
  initializeWithin(document);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
